import { Dayjs, duration } from 'dayjs'
import { Duration } from 'dayjs/plugin/duration'
import stringify from 'fast-safe-stringify'

import { DATE_TEMPLATES, format } from '~/astra'
import { getFlag } from '~/hooks/useFlag'
import { Location, LocationServiceOverride } from '~/storm'
import { ClosedReason } from '~/utils/locationServiceOverride'
import { findServicePeriod } from '~/utils/servicePeriods'

// Temporary hardcoded implementation suffices.
// We can create an entity for it later when it's more apparent what kind of solution we need
const isoMonday = 1
const isoTuesday = isoMonday + 1
const isoWednesday = isoTuesday + 1
const isoThursday = isoWednesday + 1
const isoFriday = isoThursday + 1

function getDuration(openingHours: { open: string; close: string }) {
    const [openingHour, openingMinute] = openingHours.open.split(':')
    const [closingHour, closingMinute] = openingHours.close.split(':')

    const duration_h = Number(closingHour) - Number(openingHour) + (Number(closingMinute) - Number(openingMinute)) / 60
    return duration({ hours: duration_h })
}

function getMockOpeningHours(date: Dayjs, _location: Location) {
    if (
        date.isoWeekday() === isoMonday ||
        date.isoWeekday() === isoTuesday ||
        date.isoWeekday() === isoWednesday ||
        date.isoWeekday() === isoThursday ||
        date.isoWeekday() === isoFriday
    ) {
        return { open: '8:00', close: '15:30' }
    }
}

export type OpeningHours = {
    open: string
    close: string
    duration: Duration
    changeDuration?: Duration
    lunchDuration?: Duration
    isClosed: boolean
    closedReason: ClosedReason | undefined
}

function getClosedStatus({
    locationServiceOverride,
    servicePeriod,
}: {
    locationServiceOverride: LocationServiceOverride | undefined
    servicePeriod: ReturnType<typeof findServicePeriod>
}): { isClosed: boolean; closedReason: ClosedReason | undefined } {
    // Does not disclose any closures if the feature is disabled
    if (!getFlag('closed_location_days__enabled')) {
        return { isClosed: false, closedReason: undefined }
    }

    // Location service overrides take precedence over service periods
    if (locationServiceOverride) {
        return { isClosed: true, closedReason: locationServiceOverride.closed_reason }
    }

    if (servicePeriod?.is_closed) {
        return { isClosed: true, closedReason: 'service_period' }
    }

    return { isClosed: false, closedReason: undefined }
}

export function getOpeningHours({
    date,
    location,
    locationServiceOverride,
}: {
    date: Dayjs
    location: Location
    locationServiceOverride: LocationServiceOverride | undefined
}): OpeningHours {
    const servicePeriod = findServicePeriod({ date, location })

    const { isClosed, closedReason } = getClosedStatus({ locationServiceOverride, servicePeriod })

    // Opening hours stay nominal regardless of closure: service period hours, else the mock fallback.
    let openingHours: { open: string; close: string } | undefined
    if (servicePeriod) {
        openingHours = {
            open: servicePeriod.open,
            close: servicePeriod.close,
            ...(servicePeriod.change_duration ? { changeDuration: duration(servicePeriod.change_duration) } : undefined),
            ...(servicePeriod.lunch_duration ? { lunchDuration: duration(servicePeriod.lunch_duration) } : undefined),
        }
    } else {
        openingHours = getMockOpeningHours(date, location)
    }

    if (openingHours === undefined) {
        console.error(
            'Opening hours not known for location:',
            stringify({
                room_code: location.room_code,
                date: format(date, DATE_TEMPLATES.iso),
            })
        )

        return { open: '', close: '', duration: duration({ hours: 0 }), isClosed, closedReason }
    }
    return { ...openingHours, duration: getDuration(openingHours), isClosed, closedReason }
}
